import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { listCompanies, Company } from '../../services/coreCompaniesApi';
import {
  listSurveyDispatches,
  SurveyDispatchReportItem,
  SurveyDispatchType,
} from '../../services/surveyDispatchReportApi';

const PAGE_SIZE = 25;

const STATUS_LABELS: Record<string, string> = {
  Sent: 'Enviado',
  Responded: 'Respondido',
  Pending: 'Pendente',
  Failed: 'Falhou',
  Skipped: 'Ignorado',
};

function fromDateStartUtc(value: string): string | undefined {
  if (!value) return undefined;
  return new Date(`${value}T00:00:00.000Z`).toISOString();
}

function fromDateEndUtc(value: string): string | undefined {
  if (!value) return undefined;
  return new Date(`${value}T23:59:59.999Z`).toISOString();
}

function formatDateTime(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR');
}

type SurveyDispatchReportPageProps = {
  surveyType: SurveyDispatchType;
  title: string;
  description: string;
  emptyMessage: string;
  loadErrorMessage: string;
};

export function SurveyDispatchReportPage({
  surveyType,
  title,
  description,
  emptyMessage,
  loadErrorMessage,
}: SurveyDispatchReportPageProps) {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [items, setItems] = useState<SurveyDispatchReportItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [companyId, setCompanyId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [appliedCompanyId, setAppliedCompanyId] = useState('');
  const [appliedFromDate, setAppliedFromDate] = useState('');
  const [appliedToDate, setAppliedToDate] = useState('');

  useEffect(() => {
    void listCompanies({ activeOnly: true, page: 1, pageSize: 500 })
      .then((result) => setCompanies(result.items))
      .catch(() => setCompanies([]));
  }, []);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        const result = await listSurveyDispatches({
          surveyType,
          companyId: appliedCompanyId || undefined,
          fromUtc: fromDateStartUtc(appliedFromDate),
          toUtc: fromDateEndUtc(appliedToDate),
          page,
          pageSize: PAGE_SIZE,
        });
        setItems(result.items);
        setTotalCount(result.totalCount);
        setPage(result.page);
      } catch {
        toast.error(loadErrorMessage);
      } finally {
        setLoading(false);
      }
    })();
  }, [page, appliedCompanyId, appliedFromDate, appliedToDate, surveyType, loadErrorMessage]);

  function handleFilterSubmit(event: FormEvent): void {
    event.preventDefault();
    setPage(1);
    setAppliedCompanyId(companyId);
    setAppliedFromDate(fromDate);
    setAppliedToDate(toDate);
  }

  function handleClearFilters(): void {
    setCompanyId('');
    setFromDate('');
    setToDate('');
    setPage(1);
    setAppliedCompanyId('');
    setAppliedFromDate('');
    setAppliedToDate('');
  }

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return (
    <PageShell>
      <PageHeader title={title} description={description} />

      <form
        onSubmit={handleFilterSubmit}
        className="mb-4 flex flex-wrap items-end gap-3 rounded-xl border border-secondary-200 bg-white p-4"
      >
        <label className="flex min-w-[12rem] flex-1 flex-col gap-1 text-sm">
          <span className="text-secondary-600">Empresa</span>
          <select
            className="erp-input"
            value={companyId}
            onChange={(event) => setCompanyId(event.target.value)}
          >
            <option value="">Todas</option>
            {companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex min-w-[10rem] flex-col gap-1 text-sm">
          <span className="text-secondary-600">De</span>
          <input
            type="date"
            className="erp-input"
            value={fromDate}
            onChange={(event) => setFromDate(event.target.value)}
          />
        </label>
        <label className="flex min-w-[10rem] flex-col gap-1 text-sm">
          <span className="text-secondary-600">Até</span>
          <input
            type="date"
            className="erp-input"
            value={toDate}
            onChange={(event) => setToDate(event.target.value)}
          />
        </label>
        <div className="flex gap-2">
          <button type="submit" className="erp-btn-primary">
            Filtrar
          </button>
          <button type="button" className="erp-btn-secondary" onClick={handleClearFilters}>
            Limpar
          </button>
        </div>
      </form>

      {loading && items.length === 0 ? (
        <LoadingSpinner />
      ) : !items.length ? (
        <p className="text-sm text-secondary-500">{emptyMessage}</p>
      ) : (
        <>
          <div
            className={`overflow-x-auto rounded-xl border border-secondary-200 bg-white ${loading ? 'opacity-60' : ''}`}
          >
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-secondary-100 bg-secondary-50 text-xs uppercase text-secondary-500">
                <tr>
                  <th className="px-4 py-3">Enviado em</th>
                  <th className="px-4 py-3">Empresa</th>
                  <th className="px-4 py-3">Chamado</th>
                  <th className="px-4 py-3">Destinatário</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Nota</th>
                  <th className="px-4 py-3">Respondido em</th>
                  <th className="px-4 py-3">Comentário</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-100">
                {items.map((item) => (
                  <tr key={item.id}>
                    <td className="whitespace-nowrap px-4 py-3">{formatDateTime(item.sentAtUtc)}</td>
                    <td className="px-4 py-3">{item.companyName ?? '—'}</td>
                    <td className="px-4 py-3">
                      {item.serviceTicketId && item.ticketCode ? (
                        <Link
                          to={`/customer-success/desk?selected=${item.serviceTicketId}`}
                          className="text-primary-700 hover:underline"
                        >
                          {item.ticketCode}
                        </Link>
                      ) : (
                        item.ticketCode ?? '—'
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-secondary-900">{item.contactName ?? '—'}</p>
                      <p className="text-xs text-secondary-500">{item.recipientEmail ?? ''}</p>
                    </td>
                    <td className="px-4 py-3">{STATUS_LABELS[item.status] ?? item.status}</td>
                    <td className="px-4 py-3">{item.score != null ? item.score : '—'}</td>
                    <td className="whitespace-nowrap px-4 py-3">
                      {formatDateTime(item.respondedAtUtc)}
                    </td>
                    <td className="max-w-xs truncate px-4 py-3" title={item.comment ?? undefined}>
                      {item.comment?.trim() || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ListPaginationBar
            totalCount={totalCount}
            page={page}
            pageSize={PAGE_SIZE}
            onPrevious={() => setPage((current) => Math.max(1, current - 1))}
            onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
          />
        </>
      )}
    </PageShell>
  );
}
